import { DT, GRAVITY } from "../constants.ts";
import { KITS } from "../heroes/roster/index.ts";
import { flatBasis, scale, sub } from "../math.ts";
import { Btn, type InputCmd } from "../net/input.ts";
import { MoveKind } from "./components.ts";
import type { Sim } from "./sim.ts";

const AIR_ACCEL = 14;
const GROUND_EXT_DECAY = Math.exp(-10 * DT);
const AIR_EXT_DECAY = Math.exp(-0.6 * DT);
const CROUCH_FACTOR = 0.7;

export interface MoveMods {
  speedMul: number;
  gravityMul: number;
  /** Extra vertical acceleration (m/s²), e.g. jet hover. */
  lift: number;
  canJump: boolean;
}

/** Character movement for one tick. Shared by server and client prediction. */
export function stepMovement(sim: Sim, eid: number, input: InputCmd, _pressed: number, disabled: boolean) {
  const c = sim.c;
  const def = sim.hero(eid);
  const feet = sim.posOf(eid);

  const wantCrouch = !!(input.buttons & Btn.Crouch) && !disabled && c.Move.ticks[eid]! <= 0;
  if (wantCrouch !== !!c.Player.crouch[eid]) {
    const h = wantCrouch ? def.height * CROUCH_FACTOR : def.height;
    if (sim.physics.setCapsuleHeight(eid, feet, h)) c.Player.crouch[eid] = wantCrouch ? 1 : 0;
  }

  const kind = c.Move.kind[eid]! as MoveKind;
  let scripted = false;
  if (c.Move.ticks[eid]! > 0 && kind !== MoveKind.None) {
    scripted = true;
    c.Move.ticks[eid] = c.Move.ticks[eid]! - 1;
    switch (kind) {
      case MoveKind.Dash:
      case MoveKind.Launch:
      case MoveKind.Charge: {
        const yawSteer = kind === MoveKind.Charge;
        if (yawSteer) {
          // Charge steers slowly toward the view direction.
          const { fwd } = flatBasis(input.yaw);
          const sp = c.Move.speed[eid]!;
          const cx = c.Move.x[eid]! / sp;
          const cz = c.Move.z[eid]! / sp;
          const nx = cx + (fwd.x - cx) * 0.04;
          const nz = cz + (fwd.z - cz) * 0.04;
          const l = Math.hypot(nx, nz) || 1;
          c.Move.x[eid] = (nx / l) * sp;
          c.Move.z[eid] = (nz / l) * sp;
        }
        c.Vel.x[eid] = c.Move.x[eid]!;
        c.Vel.z[eid] = c.Move.z[eid]!;
        c.Vel.y[eid] = kind === MoveKind.Dash ? c.Move.y[eid]! : kind === MoveKind.Launch ? c.Move.y[eid]! - GRAVITY * DT : c.Vel.y[eid]! - GRAVITY * DT;
        break;
      }
      case MoveKind.Grapple:
      case MoveKind.Leap:
      case MoveKind.Pulled: {
        const center = def.height * 0.5;
        const dx = c.Move.x[eid]! - feet.x;
        const dy = c.Move.y[eid]! - (feet.y + center);
        const dz = c.Move.z[eid]! - feet.z;
        const d = Math.hypot(dx, dy, dz);
        const sp = c.Move.speed[eid]!;
        if (d < 1.2 || d < sp * DT) {
          c.Move.ticks[eid] = 0;
          c.Vel.x[eid] = (dx / (d || 1)) * sp * 0.4;
          c.Vel.y[eid] = kind === MoveKind.Pulled ? 0 : Math.max(2, (dy / (d || 1)) * sp * 0.4);
          c.Vel.z[eid] = (dz / (d || 1)) * sp * 0.4;
        } else {
          c.Vel.x[eid] = (dx / d) * sp;
          c.Vel.y[eid] = (dy / d) * sp;
          c.Vel.z[eid] = (dz / d) * sp;
        }
        break;
      }
      case MoveKind.Flight: {
        c.Vel.x[eid] = c.Move.x[eid]!;
        c.Vel.y[eid] = c.Move.y[eid]!;
        c.Vel.z[eid] = c.Move.z[eid]!;
        break;
      }
      case MoveKind.Swing: {
        const anchor = { x: c.Move.x[eid]!, y: c.Move.y[eid]!, z: c.Move.z[eid]! };
        const center = { x: feet.x, y: feet.y + def.height * 0.5, z: feet.z };
        const radial = sub(center, anchor);
        const len = Math.hypot(radial.x, radial.y, radial.z) || 1;
        const n = scale(radial, 1 / len);
        const { fwd } = flatBasis(input.yaw);
        const pump = input.moveZ * 6 * DT;
        c.Vel.x[eid] = c.Vel.x[eid]! + n.x * (-GRAVITY * DT) + fwd.x * pump;
        c.Vel.y[eid] = c.Vel.y[eid]! - GRAVITY * DT;
        c.Vel.z[eid] = c.Vel.z[eid]! + n.z * (-GRAVITY * DT) + fwd.z * pump;
        const vr = c.Vel.x[eid]! * n.x + c.Vel.y[eid]! * n.y + c.Vel.z[eid]! * n.z;
        c.Vel.x[eid] = c.Vel.x[eid]! - n.x * vr;
        c.Vel.y[eid] = c.Vel.y[eid]! - n.y * vr;
        c.Vel.z[eid] = c.Vel.z[eid]! - n.z * vr;
        break;
      }
      default:
        if (kind >= MoveKind.Hero && kind <= 31) KITS[def.id].scripted?.(sim, eid, input, kind);
        else c.Move.ticks[eid] = 0;
    }
    if (c.Move.ticks[eid] === 0) c.Move.kind[eid] = MoveKind.None;
  }

  if (!scripted) {
    const mods: MoveMods = { speedMul: 1, gravityMul: 1, lift: 0, canJump: true };
    KITS[def.id].movement?.(sim, eid, input, mods);
    const canMove = !disabled && c.Status.root[eid]! <= 0;
    const { fwd, right } = flatBasis(input.yaw);
    let wx = canMove ? fwd.x * input.moveZ + right.x * input.moveX : 0;
    let wz = canMove ? fwd.z * input.moveZ + right.z * input.moveX : 0;
    const wl = Math.hypot(wx, wz);
    if (wl > 1) {
      wx /= wl;
      wz /= wl;
    }
    let speed = def.speed * mods.speedMul;
    if (c.Player.crouch[eid]) speed *= 0.55;
    if (c.Status.slow[eid]! > 0) speed *= 1 - c.Status.slowPct[eid]!;
    if (c.Status.haste[eid]! > 0) speed *= 1 + c.Status.hastePct[eid]!;
    const tx = wx * speed;
    const tz = wz * speed;
    if (c.Player.grounded[eid]) {
      c.Vel.x[eid] = tx;
      c.Vel.z[eid] = tz;
    } else {
      const ddx = tx - c.Vel.x[eid]!;
      const ddz = tz - c.Vel.z[eid]!;
      const dl = Math.hypot(ddx, ddz);
      const maxD = AIR_ACCEL * DT;
      const k = dl > maxD ? maxD / dl : 1;
      c.Vel.x[eid] = c.Vel.x[eid]! + ddx * k;
      c.Vel.z[eid] = c.Vel.z[eid]! + ddz * k;
    }
    if (canMove && mods.canJump && c.Player.grounded[eid] && input.buttons & Btn.Jump && c.Vel.y[eid]! <= 0.01) {
      c.Vel.y[eid] = def.jumpSpeed;
      c.Player.grounded[eid] = 0;
    }
    c.Vel.y[eid] = c.Vel.y[eid]! + (mods.lift - GRAVITY * mods.gravityMul) * DT;
    if (c.Vel.y[eid]! < -40) c.Vel.y[eid] = -40;
  }

  const desired = {
    x: (c.Vel.x[eid]! + c.Ext.x[eid]!) * DT,
    y: c.Vel.y[eid]! * DT,
    z: (c.Vel.z[eid]! + c.Ext.z[eid]!) * DT,
  };
  const r = sim.physics.move(eid, feet, desired);
  c.Pos.x[eid] = feet.x + r.dx;
  c.Pos.y[eid] = feet.y + r.dy;
  c.Pos.z[eid] = feet.z + r.dz;

  const wantH = Math.hypot(desired.x, desired.z);
  const gotH = Math.hypot(r.dx, r.dz);
  c.Move.blocked[eid] = scripted && wantH > 1e-3 && gotH < wantH * 0.4 ? 1 : 0;

  c.Player.grounded[eid] = r.grounded && c.Vel.y[eid]! <= 0.01 ? 1 : 0;
  if (c.Player.grounded[eid] && c.Vel.y[eid]! < 0) c.Vel.y[eid] = 0;
  if (desired.y > 0 && r.dy < desired.y * 0.5) c.Vel.y[eid] = Math.min(0, c.Vel.y[eid]!);

  const decay = c.Player.grounded[eid] ? GROUND_EXT_DECAY : AIR_EXT_DECAY;
  c.Ext.x[eid] = Math.abs(c.Ext.x[eid]! * decay) < 0.05 ? 0 : c.Ext.x[eid]! * decay;
  c.Ext.z[eid] = Math.abs(c.Ext.z[eid]! * decay) < 0.05 ? 0 : c.Ext.z[eid]! * decay;
}
